import { useEffect, useState, type FormEvent } from 'react';
import {
  Avatar,
  Button,
  FieldRow,
  Input,
  Modal,
  initialsFromName,
} from '../../components';
import { ApiError, apiRequest } from '../../lib/api';
import { useAuthStore, type SessionUser } from '../../lib/auth-store';

export type AccountProfileModalProps = {
  open: boolean;
  user: SessionUser | null;
  onClose: () => void;
};

type FormValues = {
  full_name: string;
  phone: string;
  job_title: string;
  profile_photo_url: string;
  address: string;
};

function fromUser(u: SessionUser | null): FormValues {
  return {
    full_name: u?.full_name ?? '',
    phone: u?.phone ?? '',
    job_title: u?.job_title ?? '',
    profile_photo_url: u?.profile_photo_url ?? '',
    address: u?.address ?? '',
  };
}

function formatDate(value: string | null): string {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleString();
}

function nullableTrim(value: string): string | null {
  const trimmed = value.trim();
  return trimmed.length === 0 ? null : trimmed;
}

type UpdateMeBody = {
  full_name?: string;
  phone?: string | null;
  job_title?: string | null;
  profile_photo_url?: string | null;
  address?: string | null;
};

function buildPatch(initial: FormValues, current: FormValues): UpdateMeBody {
  const patch: UpdateMeBody = {};
  if (current.full_name.trim() !== initial.full_name.trim()) {
    patch.full_name = current.full_name.trim();
  }
  if (current.phone.trim() !== initial.phone.trim()) {
    patch.phone = nullableTrim(current.phone);
  }
  if (current.job_title.trim() !== initial.job_title.trim()) {
    patch.job_title = nullableTrim(current.job_title);
  }
  if (current.profile_photo_url.trim() !== initial.profile_photo_url.trim()) {
    patch.profile_photo_url = nullableTrim(current.profile_photo_url);
  }
  if (current.address.trim() !== initial.address.trim()) {
    patch.address = nullableTrim(current.address);
  }
  return patch;
}

export function AccountProfileModal({ open, user, onClose }: AccountProfileModalProps) {
  const setUser = useAuthStore((s) => s.setUser);
  const [initial, setInitial] = useState<FormValues>(() => fromUser(user));
  const [values, setValues] = useState<FormValues>(() => fromUser(user));
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (open) {
      const seed = fromUser(user);
      setInitial(seed);
      setValues(seed);
      setSubmitting(false);
      setError(null);
      setSuccess(false);
    }
  }, [open, user]);

  const patch = buildPatch(initial, values);
  const dirty = Object.keys(patch).length > 0;
  const fullNameInvalid = values.full_name.trim().length === 0;

  async function handle(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setSuccess(false);

    if (fullNameInvalid) {
      setError('Full name is required.');
      return;
    }
    if (!dirty) {
      onClose();
      return;
    }

    setSubmitting(true);
    try {
      const { user: updated } = await apiRequest<{ user: SessionUser }>('/auth/me', {
        method: 'PATCH',
        body: patch,
      });
      setUser(updated);
      setInitial(fromUser(updated));
      setValues(fromUser(updated));
      setSuccess(true);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not save profile');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal open={open} onClose={onClose} title="My profile" width={520}>
      <form onSubmit={handle}>
        <div className="card-body" style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
            <Avatar
              initials={initialsFromName(values.full_name || user?.email || null)}
              color="var(--green-700)"
              size="lg"
            />
            <div>
              <div style={{ fontSize: 15, fontWeight: 600, color: 'var(--ink)' }}>
                {values.full_name || '—'}
              </div>
              <div className="muted" style={{ fontSize: 13 }}>
                {user?.email ?? '—'}
              </div>
            </div>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <Input
              id="profile-full-name"
              label="Full name"
              value={values.full_name}
              onChange={(v) => setValues((s) => ({ ...s, full_name: v }))}
              maxLength={200}
              required
              autoFocus
              disabled={submitting}
            />
            <Input
              id="profile-phone"
              label="Phone"
              value={values.phone}
              onChange={(v) => setValues((s) => ({ ...s, phone: v }))}
              maxLength={40}
              disabled={submitting}
            />
            <Input
              id="profile-job-title"
              label="Job title"
              value={values.job_title}
              onChange={(v) => setValues((s) => ({ ...s, job_title: v }))}
              maxLength={120}
              disabled={submitting}
            />
            <Input
              id="profile-photo-url"
              label="Profile photo URL"
              type="url"
              value={values.profile_photo_url}
              onChange={(v) => setValues((s) => ({ ...s, profile_photo_url: v }))}
              maxLength={1024}
              placeholder="https://…"
              disabled={submitting}
            />
            <Input
              id="profile-address"
              label="Address"
              value={values.address}
              onChange={(v) => setValues((s) => ({ ...s, address: v }))}
              maxLength={500}
              disabled={submitting}
            />
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <FieldRow label="Email" value={user?.email ?? '—'} />
            <FieldRow
              label="Roles"
              value={user?.roles && user.roles.length > 0 ? user.roles.join(', ') : '—'}
            />
            <FieldRow label="Status" value={user?.is_active ? 'Active' : 'Inactive'} />
            <FieldRow label="Last sign-in" value={formatDate(user?.last_login_at ?? null)} />
          </div>

          <div className="muted" style={{ fontSize: 12 }}>
            Email, roles, status, department and shift are managed by an administrator on the
            Staff page.
          </div>

          {error && <div className="alert">{error}</div>}
          {success && !error && (
            <div className="muted" style={{ fontSize: 13 }}>
              Profile updated.
            </div>
          )}
        </div>
        <div
          className="card-header"
          style={{
            justifyContent: 'flex-end',
            borderTop: '1px solid var(--border)',
            borderBottom: 'none',
          }}
        >
          <Button onClick={onClose} disabled={submitting} type="button">
            Close
          </Button>
          <Button
            variant="primary"
            type="submit"
            disabled={submitting || fullNameInvalid || !dirty}
          >
            {submitting ? 'Saving…' : 'Save changes'}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
